import { createReactBlockSpec } from "@blocknote/react";
import React, { useState, useCallback, useRef, useEffect } from "react";

const MIN_VIDEO_WIDTH = 200;
const MAX_VIDEO_WIDTH = 1200;
const MIN_VIDEO_HEIGHT = 120;
const MAX_VIDEO_HEIGHT = 600;
const DEFAULT_VIDEO_WIDTH = 640;
const DEFAULT_VIDEO_HEIGHT = 360;

type ResizeParams = {
  handleUsed: "left" | "right" | "bottom";
  initialClientX: number;
  initialClientY: number;
  initialWidth: number;
  initialHeight: number;
};

/**
 * 비디오 블록 카드: 우클릭 방지 + 다운로드 버튼 미노출
 * - controlsList="nodownload": 브라우저 기본 컨트롤에서 다운로드 버튼 숨김
 * - onContextMenu preventDefault: 우클릭 컨텍스트 메뉴(동영상 저장 등) 차단
 */
const VideoBlockCard = ({
  url,
  editable,
  width,
  onWidthChange,
  height,
  onHeightChange,
  onContextMenuBlock,
}: {
  url: string;
  editable: boolean;
  width?: number;
  onWidthChange?: (width: number) => void;
  height?: number;
  onHeightChange?: (height: number) => void;
  onContextMenuBlock: (e: React.MouseEvent) => void;
}) => {
  const [resizeParams, setResizeParams] = useState<ResizeParams | undefined>(
    undefined,
  );
  const [localWidth, setLocalWidth] = useState<number | undefined>(width);
  const [localHeight, setLocalHeight] = useState<number | undefined>(height);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalWidth(width);
  }, [width]);
  useEffect(() => {
    setLocalHeight(height);
  }, [height]);

  useEffect(() => {
    if (!resizeParams) return;

    const onMouseMove = (e: MouseEvent) => {
      if (resizeParams.handleUsed === "bottom") {
        const delta = e.clientY - resizeParams.initialClientY;
        setLocalHeight(
          Math.min(
            Math.max(resizeParams.initialHeight + delta, MIN_VIDEO_HEIGHT),
            MAX_VIDEO_HEIGHT,
          ),
        );
      } else {
        const delta =
          resizeParams.handleUsed === "left"
            ? resizeParams.initialClientX - e.clientX
            : e.clientX - resizeParams.initialClientX;
        setLocalWidth(
          Math.min(
            Math.max(resizeParams.initialWidth + delta, MIN_VIDEO_WIDTH),
            wrapperRef.current?.parentElement?.clientWidth || MAX_VIDEO_WIDTH,
          ),
        );
      }
    };

    const onMouseUp = () => {
      const handle = resizeParams.handleUsed;
      setResizeParams(undefined);
      if (handle === "bottom") {
        if (localHeight != null && onHeightChange) onHeightChange(localHeight);
      } else {
        if (localWidth != null && onWidthChange) onWidthChange(localWidth);
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [resizeParams, localWidth, localHeight, onWidthChange, onHeightChange]);

  const handleLeftDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setResizeParams({
        handleUsed: "left",
        initialWidth: wrapperRef.current?.clientWidth ?? DEFAULT_VIDEO_WIDTH,
        initialHeight: localHeight ?? DEFAULT_VIDEO_HEIGHT,
        initialClientX: e.clientX,
        initialClientY: e.clientY,
      });
    },
    [localHeight],
  );

  const handleRightDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setResizeParams({
        handleUsed: "right",
        initialWidth: wrapperRef.current?.clientWidth ?? DEFAULT_VIDEO_WIDTH,
        initialHeight: localHeight ?? DEFAULT_VIDEO_HEIGHT,
        initialClientX: e.clientX,
        initialClientY: e.clientY,
      });
    },
    [localHeight],
  );

  const handleBottomDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setResizeParams({
        handleUsed: "bottom",
        initialWidth: wrapperRef.current?.clientWidth ?? DEFAULT_VIDEO_WIDTH,
        initialHeight: localHeight ?? DEFAULT_VIDEO_HEIGHT,
        initialClientX: e.clientX,
        initialClientY: e.clientY,
      });
    },
    [localHeight],
  );

  const resizeCursor = resizeParams
    ? resizeParams.handleUsed === "bottom"
      ? "ns-resize"
      : "ew-resize"
    : "default";

  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={wrapperRef}
      className="lumir-video-block-wrapper"
      onContextMenu={onContextMenuBlock}
      style={{
        position: "relative",
        width: localWidth != null ? `${localWidth}px` : undefined,
        maxWidth: "100%",
        cursor: resizeCursor,
        transition: resizeParams ? "none" : "box-shadow 0.2s",
      }}
      onMouseEnter={() => {
        if (!resizeParams) setHovered(true);
      }}
      onMouseLeave={() => {
        if (!resizeParams) setHovered(false);
      }}
    >
      {editable && (hovered || resizeParams) && (
        <>
          <div
            className="lumir-resize-handle"
            style={{ left: "4px" }}
            onMouseDown={handleLeftDown}
          />
          <div
            className="lumir-resize-handle"
            style={{ right: "4px" }}
            onMouseDown={handleRightDown}
          />
        </>
      )}

      <div
        style={{
          width: "100%",
          height: `${localHeight ?? DEFAULT_VIDEO_HEIGHT}px`,
          overflow: "hidden",
          backgroundColor: "#000",
          borderRadius: "6px",
        }}
      >
        <video
          src={url}
          controls
          controlsList="nodownload"
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
          onContextMenu={(e) => e.preventDefault()}
        />
        {editable && (hovered || resizeParams) && (
          <div
            className="lumir-resize-handle-bottom"
            onMouseDown={handleBottomDown}
          />
        )}
      </div>
    </div>
  );
};

export const VideoBlock = createReactBlockSpec(
  {
    type: "video",
    propSchema: {
      url: { default: "" },
      previewWidth: { default: 640 },
      previewHeight: { default: 360 },
    },
    content: "none",
  },
  {
    render: (props) => {
      const url = props.block.props.url ?? "";
      const editable = props.editor.isEditable;

      const handleContextMenu = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }, []);

      if (!url) {
        return (
          <div
            className="lumir-video-block-placeholder"
            onContextMenu={handleContextMenu}
            style={{
              width: "100%",
              maxWidth: `${DEFAULT_VIDEO_WIDTH}px`,
              height: `${DEFAULT_VIDEO_HEIGHT}px`,
              backgroundColor: "#1a1a1a",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255,255,255,0.5)",
              fontSize: "14px",
            }}
          >
            비디오 URL 없음
          </div>
        );
      }

      return (
        <VideoBlockCard
          url={url}
          editable={editable}
          width={props.block.props.previewWidth}
          onWidthChange={(newWidth) => {
            props.editor.updateBlock(props.block, {
              props: { previewWidth: newWidth },
            });
          }}
          height={props.block.props.previewHeight}
          onHeightChange={(newHeight) => {
            props.editor.updateBlock(props.block, {
              props: { previewHeight: newHeight },
            });
          }}
          onContextMenuBlock={handleContextMenu}
        />
      );
    },
  },
);
